<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Floating Button -->
      <floating-button @click="orderCreate()" v-if="authUser"/>

      <!-- Confirmation -->
      <confirmation
        v-if="authUser"
        v-model="dialog"
        title="Eliminar Orden de Trabajo"
        text="Desea eliminar la orden de trabajo"
        :item="orderToDelete.code"
        @close="dialog = false"
        @confirm="deleteOrder()"
      />

      <!-- Page Header -->
      <page-header
        title="Ordenes de Trabajo"
        icon="mdi-file-document-edit"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="init(true)"
      ></page-header>

      <!-- Content -->
      <v-row align="center" justify="center">
        <!-- Table -->
        <v-col cols="12">
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <v-data-table
            v-show="authUser"
            :headers="headers"
            :items="filteredFarm"
            class="elevation-1"
            :expanded.sync="expanded"
            :options.sync="pagination"
            :server-items-length="pagination_total"
            :footer-props="{
              'items-per-page-options': [5, 15, 30, 50]
            }"
            @update:options="init()"
          >
            <!-- Top -->
            <template v-slot:top>
              <v-toolbar flat color="white">
                <!-- State Filter -->
                <v-select
                  v-model="filters.state"
                  :items="stateItems"
                  class="shrink mr-5"
                  label="Estado"
                  clearable
                  hide-details
                  single-line
                  @change="init()"
                ></v-select>
                <!-- Work Type Filter -->
                <v-select
                  v-model="filters.work_type_id"
                  :items="workTypes"
                  class="shrink mr-5"
                  label="Tipo de trabajo"
                  clearable
                  hide-details
                  single-line
                  @change="init()"
                ></v-select>
                <!-- Technician Filter -->
                <v-autocomplete
                  v-model="filters.technician_id"
                  :items="technicians"
                  label="Operario"
                  class="grow mr-5"
                  :item-text="text"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
                <!-- Farm Filter -->
                <v-autocomplete
                  v-model="filters.wind_farm_id"
                  :items="farms"
                  label="Parque"
                  class="grow mr-5"
                  item-text="name"
                  item-value="id"
                  clearable
                  single-line
                  hide-details 
                  @change="init()"
                ></v-autocomplete>
                <!-- Contract Award Filter -->
                <v-autocomplete
                  v-model="filters.contract_award_id"
                  :items="contracts"
                  label="Adjudicación"
                  class="grow mr-5"
                  item-text="client_code"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                  @change="init()"
                ></v-autocomplete>
              </v-toolbar>
            </template>
            <!-- Work Type -->
            <template v-slot:item.work_type_id="{ item }">
              {{ workType[item.work_type_id] }}
            </template>
            <!-- Operators -->
            <template v-slot:item.technicians="{ item }">
              <div
                v-for="(item, idx) in item.technicians"
                :key="item.id"
              >
                <v-chip class="ma-1" light v-if="idx < 2">{{ operatorName(item) }}</v-chip>
              </div>
              <div class="pb-1" v-if="item.technicians.length > 2" >({{ item.technicians.length - 2 }} más)</div>
            </template>
            <!-- Contract Award -->
            <template v-slot:item.contract_award="{ item }">
              {{ item.contract_award.client_code }}
            </template>
            <!-- Farms -->
            <template v-slot:item.wind_farms="{ item }">
              <div
                v-for="(item, idx) in item.wind_farms"
                :key="item.id"
              >
                <v-chip class="ma-1" light v-if="idx < 2">{{ item.name }}</v-chip>
              </div>
              <div class="pb-1" v-if="item.wind_farms.length > 2" >({{ item.wind_farms.length - 2 }} más)</div>
            </template>
            <!-- State -->
            <template v-slot:item.state="{ item }">
              <v-row justify="center" align="center" align-center>
                <v-chip
                  :class="item.state === 'PENDIENTE' ? '' : 'white--text'"
                  :color="item.state === 'EN CURSO' ? 'primary' : item.state === 'FINALIZADA' ? 'deep-orange' : ''"
                >{{ item.state}}</v-chip>
              </v-row>
            </template>
            <!-- Actions -->
            <template v-slot:item.action="{ item }">
              <v-icon class="mr-2" @click="orderEdit(item.id)">mdi-pencil</v-icon>
              <v-icon @click="deleteOrderDialog(item)">mdi-delete</v-icon>
            </template>
            <!-- Expand Icon -->
            <template v-slot:item.expand="{ item }">
              <v-icon
                :class="{'rotate': expanded.includes(item)}"
                icon
                @click="expanded.includes(item) ? expanded = [] : expanded = [item]"
              >mdi-chevron-down</v-icon>
            </template>
            <!-- Expanded Row -->
            <template v-slot:expanded-item="{ headers, item }">
              <!-- Summary -->
              <td :colspan="3" class="text-center py-3">
                <div class="mb-3 font-weight-bold caption">Resumen</div>
                {{ item.summary }}
              </td>
              <!-- Farms -->
              <td class="text-center py-3" :colspan="1">
                <span class="mx-1" v-for="(item, idx) in item.wind_farms" :key="idx">
                  <v-chip class="my-1 text-capitalize" v-if="idx >= 2">{{ item.name }}</v-chip>
                </span>
              </td>
              <!-- Operators -->
              <td class="text-center py-3" :colspan="1">
                <span class="mx-1" v-for="(item, idx) in item.technicians" :key="idx">
                  <v-chip class="my-1 text-capitalize" v-if="idx >= 2">{{ operatorName(item) }}</v-chip>
                </span>
              </td>
              <td :colspan="3"></td>
            </template>
            <!-- No Data -->
            <template v-slot:no-data>
              No hay elementos
            </template>
            <!-- No Results -->
            <template v-slot:no-results>
              No hay elementos
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      
    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import Confirmation from "@/components/Confirmation";
import PageHeader from "@/components/PageHeader";
import Util from "@/util/index";
import STATES from '@/models/States'

export default {
  components: {
    FloatingButton,
    Confirmation,
    PageHeader
  },
  data: () => ({
    expanded: [],
    authUser: false,
    loadFilters: true,
    isLoadingData: false,
    dialog: false,
    stateItems: STATES.WORK_ORDER_STATE,
    workType: STATES.WORK_ORDER_TYPE,
    workTypes: STATES.WORK_ORDER_TYPES,
    orderToDelete: {},
    filters: {
      state: '',
      wind_farm_id: '',
      work_type_id: '',
      contract_award_id: '',
      technician_id: ''
    },
    pagination: {
      page: 1,
      itemsPerPage: 5,
      sortBy: [],
      sortDesc: []
    },
    pagination_total: -1,
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Ordenes de Trabajo", disabled: true }
      ]
    },
    headers: [
      { text: "", value: "expand", align: "center", sortable: false },
      { text: "Código OT", value: "code", align: "center" },
      { text: "Tipo de trabajo", value: "work_type_id", align: "center" },
      { text: "Parques", value: "wind_farms", align: "center", sortable: false },
      { text: "Operarios", value: "technicians", align: "center", sortable: false },
      { text: "Adjudicación", value: "contract_award.client_code", align: "center" },
      { text: "Estado", value: "state", align: "center", sortable: false },
      { text: "Acciones", value: "action", align: "center", sortable: false }
    ],
  }),
  computed: {
    orders () {
      return this.$store.state.orders.work_orders
    },
    farms () {
      return this.$store.state.farms.wind_farms.filter(element => {
        return element.is_active === true
      })
    },
    technicians () {
      return this.$store.state.users.all.filter(element => {
        return element.roles.filter(e => e.name === 'Operario').length > 0 && element.isActive === true
      })
    },
    contracts () {
      return this.$store.state.contracts.contracts
    },
    filteredState () {
      return this.filterState
        ? this.orders.filter(element => {
          return element.state === this.filterState
        }) : this.orders
    },
    filteredType () {
      return this.filterType
        ? this.filteredState.filter(element => {
          return element.work_type === this.filterType
        }) : this.filteredState
    },
    filteredTechnicias () {
      return this.filterTechnician
        ? this.filteredType.filter(element => {
            return element.technicians.some(element => {
              return element.id === this.filterTechnician
            })
          }) : this.filteredType
    },
    filteredFarm () {
      return this.filterFarm
        ? this.filteredTechnicias.filter(element => {
          return element.wind_farms.some(element => {
            return element.id === this.filterFarm
          })
        }) : this.filteredTechnicias
    }
  },
  methods: {
    text (item) {
      if (item.lastName2) {
        return item.firstName + ' ' + item.lastName1 + ' ' + item.lastName2
      } else {
        return item.firstName + ' ' + item.lastName1
      }
    },
    operatorName (item) {
      if (item.lastName2) {
        return item.first_name + ' ' + item.last_name_1 + ' ' + item.last_name_2
      } else {
        return item.first_name + ' ' + item.last_name_1
      }
    },
    deleteOrderDialog (order) {
      this.orderToDelete = order
      this.dialog = !this.dialog
    },
    orderCreate () {
      this.$router.push({
        name: "order-create"
      });
    },
    orderEdit (id) {
      this.$router.push({
        name: "order-edit",
        params: { id: id }
      });
    },
    deleteOrder () {
      var id = this.orderToDelete.id
      this.$store.dispatch('orders/deleteOrder', id)
        .then(() => {
          this.$toast("Orden de trabajo borrada correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.init()
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error eliminando la orden de trabajo."
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.dialog = !this.dialog
        })
    },
    init (loadFilters) {
      this.isLoadingData = true;
      const body = {
        page: this.pagination.page,
        perPage: this.pagination.itemsPerPage,
        filters: this.filters,
        with: 'technicians,windFarms,contractAward'
      }
      // Sort
      if (this.pagination.sortDesc.length) body.sortType = this.pagination.sortDesc[0] ? 'desc' : 'asc'
      if (this.pagination.sortBy.length) {
        const sortBy = this.pagination.sortBy[0]
        if (sortBy === 'contract_award.client_code') body.sortBy = 'contract_award'
        else body.sortBy = sortBy
      }
      this.$store.dispatch('orders/getOrders', body)
        .then(res => {
          this.authUser = true
          const meta = res.data.meta
          this.pagination_total = meta.total
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando las ordenes de trabajo."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          if (this.loadFilters || loadFilters) this.loadUsers()
          else if (!this.loadFilters) this.isLoadingData = false
          this.loadFilters = false
        })
    },
    loadUsers() {
      this.$store
        .dispatch("users/getAllUsers")
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de operarios."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.loadFarms()
        });
    },
    loadFarms () {
      this.$store.dispatch('farms/getFarms')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista parques eólicos."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.loadContracts();
        });
    },
    loadContracts () {
      this.isLoadingData = true;
      this.$store.dispatch('contracts/getContracts')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de adjudicaciones."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false;
        });
    },
  },
  created () {
    // this.init()
  }
}
</script>
